import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LEGAL_PATHS } from "../../legal/legalPaths";
import "./legal-components.css";

/**
 * Cinematic purchase-rights acknowledgment panel.
 * Parent gates checkout / mint using `onValidityChange` booleans.
 */
export default function PurchaseLegalAcknowledgment({
  showMerchCheckbox = false,
  showVrCheckbox = false,
  /** Oshar VR real estate / digital access checkout: Stripe-friendlier copy without comic-merch license in the core line. */
  digitalAccessPurchaseTerms = false,
  className = "",
  onValidityChange,
}) {
  const [core, setCore] = useState(false);
  const [merch, setMerch] = useState(false);
  const [vr, setVr] = useState(false);

  const cbRef = useRef(onValidityChange);
  cbRef.current = onValidityChange;

  useEffect(() => {
    const merchOk = !showMerchCheckbox || merch;
    const vrOk = !showVrCheckbox || vr;
    cbRef.current?.({
      libraryDigitalOk: core,
      libraryNftOk: core && merchOk,
      vrPurchaseOk: core && vrOk,
    });
  }, [core, merch, vr, showMerchCheckbox, showVrCheckbox]);

  const anyUnchecked =
    !core || (showMerchCheckbox && !merch) || (showVrCheckbox && !vr);

  return (
    <section
      className={`td-purchase-legal ${className}`.trim()}
      aria-labelledby="td-purchase-legal-title"
    >
      <div className="td-purchase-legal__frame" aria-hidden />
      <h3 id="td-purchase-legal-title" className="td-purchase-legal__title">
        Rights confirmation
      </h3>
      <p className="td-purchase-legal__lede">
        Confirm the instruments that apply before funds move or an on-chain transaction is requested.
      </p>

      <label className="td-purchase-legal__row">
        <input
          type="checkbox"
          checked={core}
          onChange={(e) => setCore(e.target.checked)}
        />
        <span>
          {digitalAccessPurchaseTerms ? (
            <>
              I agree to Treydora&apos;s{" "}
              <Link to={LEGAL_PATHS.terms} target="_blank" rel="noreferrer">
                Terms of Use
              </Link>
              ,{" "}
              <Link to={LEGAL_PATHS.digitalAssetRisk} target="_blank" rel="noreferrer">
                Digital Asset Risk Disclosure
              </Link>
              ,{" "}
              <Link to={LEGAL_PATHS.refundPolicy} target="_blank" rel="noreferrer">
                Refund Policy
              </Link>
              , and the applicable{" "}
              <Link to={LEGAL_PATHS.vrRealEstateTerms} target="_blank" rel="noreferrer">
                digital access/license terms for this purchase
              </Link>
              .
            </>
          ) : (
            <>
              I agree to Treydora&apos;s{" "}
              <Link to={LEGAL_PATHS.terms} target="_blank" rel="noreferrer">
                Terms of Use
              </Link>
              ,{" "}
              <Link to={LEGAL_PATHS.digitalAssetRisk} target="_blank" rel="noreferrer">
                Digital Asset Risk Disclosure
              </Link>
              ,{" "}
              <Link to={LEGAL_PATHS.refundPolicy} target="_blank" rel="noreferrer">
                Refund Policy
              </Link>
              , and the applicable NFT / digital license terms for this purchase (including{" "}
              <Link to={LEGAL_PATHS.nftComicMerchLicense} target="_blank" rel="noreferrer">
                NFT Comic Merchandiser Terms
              </Link>{" "}
              where relevant).
            </>
          )}
        </span>
      </label>

      {showVrCheckbox ? (
        <label className="td-purchase-legal__row">
          <input
            type="checkbox"
            checked={vr}
            onChange={(e) => setVr(e.target.checked)}
          />
          <span>
            {digitalAccessPurchaseTerms ? (
              <>
                I understand this purchase grants digital/virtual access rights inside the Treydora/Oshar ecosystem
                only and does not transfer real-world real estate, land, securities, equity, ownership in a company, or
                guaranteed income.
              </>
            ) : (
              <>
                I understand this purchase grants digital / virtual access rights only and does not transfer real-world
                real estate, land, securities, equity, or guaranteed income. See{" "}
                <Link to={LEGAL_PATHS.vrRealEstateTerms} target="_blank" rel="noreferrer">
                  VR Real Estate NFT Terms
                </Link>
                .
              </>
            )}
          </span>
        </label>
      ) : null}

      {showMerchCheckbox ? (
        <label className="td-purchase-legal__row">
          <input
            type="checkbox"
            checked={merch}
            onChange={(e) => setMerch(e.target.checked)}
          />
          <span>
            I understand merchandiser rights are limited, conditional, and tied to valid ownership of the applicable
            NFT, as described in the{" "}
            <Link to={LEGAL_PATHS.nftComicMerchLicense} target="_blank" rel="noreferrer">
              NFT Comic Merchandiser Terms
            </Link>
            .
          </span>
        </label>
      ) : null}

      <p className="td-purchase-legal__hint" aria-live="polite">
        {anyUnchecked
          ? "Treydora checkout and wallet rails stay locked until each required line is acknowledged."
          : "Acknowledgments are captured in the UI for this session; Treydora may add server-side acceptance records in a future release."}
      </p>
    </section>
  );
}
